import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Disc3, Plus, Settings } from 'lucide-react';
import type { Me } from '@vinyl/shared';
import { HelpButton } from '../help/help.tsx';
import { SearchField } from '../records/SearchField.tsx';

const NAV = [
  { to: '/collection', key: 'nav.collection', icon: Disc3, help: 'shell.navCollection' },
  { to: '/add', key: 'nav.add', icon: Plus, help: 'shell.navAdd' },
  { to: '/settings', key: 'nav.settings', icon: Settings, help: 'shell.navSettings' },
] as const;

function UserBadge({ me }: { me: Me }) {
  return (
    <div className="flex items-center gap-2" data-help-id="shell.user">
      <span className="hidden text-sm font-medium lg:inline">{me.name}</span>
      <span
        aria-hidden
        className="flex size-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
      >
        {me.initials}
      </span>
      <span className="sr-only lg:hidden">{me.name}</span>
    </div>
  );
}

/**
 * The frame of every signed-in page (app-shell spec): bottom tabs below
 * 768px, sidebar and top bar above.
 */
export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-dvh md:grid md:grid-cols-[15rem_1fr]">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground">
        {t('shell.skipToContent')}
      </a>
      <aside className="hidden border-r border-border bg-card md:flex md:flex-col md:gap-6 md:p-5" aria-label={t('shell.sidebar')}>
        <Link to="/collection" className="font-display text-2xl text-primary" data-help-id="shell.home">
          {t('app.name')}
        </Link>
        <nav aria-label={t('shell.mainNav')}>
          <ul className="flex flex-col gap-1">
            {NAV.map(({ to, key, icon: Icon, help }) => (
              <li key={to}>
                <Link
                  to={to}
                  data-help-id={help}
                  className="flex items-center gap-3 rounded-full px-4 py-2 text-sm font-medium hover:bg-accent"
                  activeProps={{ className: 'bg-accent text-primary', 'aria-current': 'page' }}
                >
                  <Icon aria-hidden className="size-4" />
                  {t(key)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="flex min-h-dvh min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur md:px-6">
          <span className="font-display text-xl text-primary md:hidden">{t('app.name')}</span>
          <div className="hidden flex-1 md:block">
            <SearchField />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <HelpButton />
            <UserBadge me={me} />
          </div>
        </header>

        <main id="main" className="flex-1 px-4 pb-24 pt-6 md:px-8 md:pb-10">
          {children}
        </main>

        <nav aria-label={t('shell.mainNav')} className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card md:hidden">
          <ul className="grid grid-cols-3">
            {NAV.map(({ to, key, icon: Icon, help }) => (
              <li key={to}>
                <Link
                  to={to}
                  data-help-id={help}
                  className="flex min-h-14 flex-col items-center justify-center gap-1 text-xs font-medium text-muted-foreground"
                  activeProps={{ className: 'text-primary', 'aria-current': 'page' }}
                >
                  <Icon aria-hidden className="size-5" />
                  {t(key)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}
